import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { Me } from '../api.ts';
import { activeItemTo, itemsOf, locate, type NavArea } from '../navigation.ts';
import { usePlatformLook } from '../platform.tsx';
import { useSlotContent } from './slots.tsx';
import { UserLinks } from './UserLinks.tsx';
import { AdminBadge } from './AdminBadge.tsx';

/**
 * Layout "Planilha" (ADR 0128): as áreas em abas, como num programa de planilha; embaixo, as
 * telas da área e a faixa de botões que a tela aberta põe no lugar `ribbon`.
 */
export function SheetBar({
  areas,
  me,
  onSearch,
  compact,
}: {
  areas: NavArea[];
  me: Me;
  onSearch: () => void;
  compact: boolean;
}) {
  const { t } = useTranslation();
  const look = usePlatformLook();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current = locate(areas, pathname).area;
  const activeTo = activeItemTo(areas, pathname);
  const ribbon = useSlotContent('ribbon');
  const screens = current && current.id !== 'home' && !current.bottom ? itemsOf(current) : [];
  return (
    <div className="hidden shrink-0 flex-col md:flex">
      <div className="ui-sidebar flex items-end gap-1 px-3 pt-1.5">
        <Link to="/" aria-label={t('nav.homeBrand')} className="mr-2 mb-1.5 flex shrink-0">
          <img src={look.symbolUrl} alt="" className="h-6 w-auto max-w-10 object-contain" />
        </Link>
        <span className="mr-2 mb-1.5 flex">
          <AdminBadge compact />
        </span>
        <nav
          aria-label={t('nav.label')}
          className="flex min-w-0 flex-1 items-end gap-0.5 overflow-x-auto"
        >
          {areas
            .filter((a) => !a.bottom)
            .map((area) => {
              const active = area.id === current?.id;
              return (
                <Link
                  key={area.id}
                  to={itemsOf(area)[0]!.to}
                  aria-current={active ? 'true' : undefined}
                  className={`flex h-8 shrink-0 items-center rounded-t-md px-3 text-sm whitespace-nowrap ${
                    active ? 'bg-sheet font-medium text-ink' : 'hover:bg-paper hover:text-ink'
                  }`}
                >
                  {t(area.key)}
                </Link>
              );
            })}
        </nav>
        <div className="mb-1">
          <UserLinks areas={areas} me={me} onSearch={onSearch} />
        </div>
      </div>
      {(screens.length > 1 || ribbon) && (
        <div
          className={`flex items-center gap-3 overflow-x-auto border-b border-rule bg-sheet px-3 ${
            compact ? 'py-1' : 'py-2'
          }`}
        >
          {screens.length > 1 && (
            <nav aria-label={t(current!.key)} className="flex shrink-0 items-center gap-1">
              {screens.map((item) => {
                const active = item.to === activeTo;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    className={`flex min-h-8 items-center rounded-md px-2.5 text-xs whitespace-nowrap ${
                      active
                        ? 'ui-nav-active font-medium'
                        : 'text-muted hover:bg-paper hover:text-ink'
                    }`}
                  >
                    {t(item.key)}
                  </Link>
                );
              })}
            </nav>
          )}
          {ribbon && (
            <div
              className={`flex min-w-0 flex-1 items-center gap-2 ${screens.length > 1 ? 'border-l border-rule pl-3' : ''}`}
            >
              {ribbon}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
